import { computed, nextTick, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n, useUrl, useReportUtils } from '@/composables';
import { $emitter } from '@/services/emitter';
import helpers from '@/services/helpers';
import { useStore } from '@/store';

export type BookmarkIndexed = Bemob.Bookmark & {
  titleLowerCase: string;
  index: number;
};

type UseBookmarkListOptions = {
  onClose?: () => void;
};

export const useBookmarkList = (options: UseBookmarkListOptions = {}) => {
  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const urlApi = useUrl();
  const store = useStore();

  const reportUtilsCtx = reactive({
    drill: [] as Bemob.ReportQueryDrill[],
    get reportTabLink() {
      return urlApi.reportTabLink.value;
    },
    getReturnLink: urlApi.getReturnLink,
  });

  const utils = useReportUtils(reportUtilsCtx);

  const bookmarks = computed<BookmarkIndexed[]>(() =>
    store.bookmarks.map((item, index) => ({
      ...item,
      titleLowerCase: item.title ? item.title.toLowerCase() : '',
      index,
    })),
  );

  const handleDelete = (index: number) => {
    store.removeBookmark(index);
  };

  const openBookmark = (item: BookmarkIndexed) => {
    nextTick(() => {
      options.onClose?.();
    });

    return utils.resolveReportNavigation(urlApi.drilldown.value, {
      isAddTab: true,
      tabData: item.tabData,
    });
  };

  const openDeleteDialog = (index: number) => {
    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: {
        message: t('Are you sure you want to delete this bookmark?'),
        onConfirm: () => handleDelete(index),
      },
    });
  };

  const handleOpenBookmark = (item: BookmarkIndexed, immediate = false) => {
    const normalizedPathUrl = urlApi.normalizeUrl(route.fullPath);
    const normalizedItemUrl = urlApi.normalizeUrl(item.url);

    if (normalizedItemUrl === normalizedPathUrl) {
      openBookmark(item).then(() => {
        $emitter.emit('reload-lives');
        $emitter.emit('reload-conversions');
        $emitter.emit('reload-errors');
      });
    } else {
      const navigate = () => {
        router.push(item.url).then(() => {
          nextTick(() => {
            openBookmark(item);
            $emitter.emit('get-report');
          });
        });
      };

      if (immediate) {
        options.onClose?.();
        store.setLoading(true);
        helpers.afterBrowserRedraw(navigate);
      } else {
        navigate();
      }
    }
  };

  return {
    bookmarks,
    openDeleteDialog,
    handleOpenBookmark,
  };
};
